import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { AuthData } from '../../provider/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { 
  FaMapMarkerAlt, 
  FaClock, 
  FaDollarSign, 
  FaBuilding, 
  FaSearch, 
  FaFilter, 
  FaChevronUp, 
  FaChevronDown,
  FaTimes,
  FaExpand,
  FaCompress,
  FaEye,
  FaCheck,
  FaCalendar,
  FaSpinner,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { 
  EnhancedApplicationForm, 
  APPLICATION_LIMITS, 
  validateApplicationForm 
} from '../../components/EnhancedApplicationForm';

const Explore = () => {
  const { user } = AuthData();
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState({});
  const [userApplications, setUserApplications] = useState([]);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    coverLetter: '',
    whyInterested: '',
    relevantExperience: '',
    availability: '',
    additionalInfo: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    location: '',
    type: '',
    duration: '',
    isPaid: '',
    fields: [],
    searchTerm: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [areas, setAreas] = useState([]);
  
  // New state for collapsible search area
  const [searchAreaCollapsed, setSearchAreaCollapsed] = useState(false);
  const [searchAreaMinimized, setSearchAreaMinimized] = useState(true); // Default to minimized

  // Check if any filters are active
  const hasActiveFilters = filters.searchTerm || filters.location || filters.type || 
    filters.duration || filters.isPaid || filters.fields.length > 0;

  // Fetch positions from Firebase
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        setLoading(true);
        const positionsRef = collection(db, 'positions');
        const q = query(
          positionsRef, 
          where('isAdvertised', '==', true),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        
        const positionsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setPositions(positionsData);
        setFilteredPositions(positionsData);
      } catch (error) {
        console.error('Error fetching positions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPositions();
  }, []);

  useEffect(() => {
    const fetchEmployersAndApplications = async () => {
      try {
        // Fetch employers
        const employersSnapshot = await getDocs(collection(db, 'employers'));
        const employersData = {};
        employersSnapshot.docs.forEach(doc => {
          employersData[doc.id] = { id: doc.id, ...doc.data() };
        });
        setEmployers(employersData);

        // Fetch user applications if authenticated
        if (user?.userd?.uid) {
          const applicationsQuery = query(
            collection(db, 'applications'),
            where('studentId', '==', user.userd.uid)
          );
          const applicationsSnapshot = await getDocs(applicationsQuery);
          const applicationsData = applicationsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setUserApplications(applicationsData);
        }
      } catch (error) {
        console.error('Error fetching employers and applications:', error);
      }
    };

    fetchEmployersAndApplications();
  }, [user]);

  // Fetch available areas
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const areasRef = collection(db, 'settings');
        const areasDoc = await getDocs(areasRef);
        if (!areasDoc.empty) {
          const areasData = areasDoc.docs[0].data();
          setAreas(areasData.areas || []);
        }
      } catch (error) {
        console.error('Error fetching areas:', error);
      }
    };

    fetchAreas();
  }, []);

  // Filter positions based on current filters
  useEffect(() => {
    let filtered = positions;

    if (filters.searchTerm) {
      filtered = filtered.filter(position =>
        position.name?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        position.description?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        position.requiredSkills?.some(skill => 
          skill.toLowerCase().includes(filters.searchTerm.toLowerCase())
        )
      );
    }

    if (filters.location) {
      filtered = filtered.filter(position =>
        position.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    if (filters.type) {
      filtered = filtered.filter(position => position.type === filters.type);
    }

    if (filters.duration) {
      filtered = filtered.filter(position => position.duration === filters.duration);
    }

    if (filters.isPaid !== '') {
      filtered = filtered.filter(position => 
        position.isPaid === (filters.isPaid === 'true')
      );
    }

    if (filters.fields.length > 0) {
      filtered = filtered.filter(position =>
        position.fields?.some(field => filters.fields.includes(field))
      );
    }

    setFilteredPositions(filtered);
  }, [filters, positions]);

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  }, []);

  const handleFieldToggle = useCallback((field) => {
    setFilters(prev => ({
      ...prev,
      fields: prev.fields.includes(field)
        ? prev.fields.filter(f => f !== field)
        : [...prev.fields, field]
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      location: '',
      type: '',
      duration: '',
      isPaid: '',
      fields: [],
      searchTerm: ''
    });
  }, []);
  
  const getApplicationStatus = (positionId) => {
    const application = userApplications.find(app => app.positionId === positionId);
    return application ? application.status : null;
  };

  const canApplyToPosition = (positionId) => {
    const status = getApplicationStatus(positionId);
    return !status || status === 'withdrawn';
  };

  const handlePositionClick = (position) => {
    setSelectedPosition(position);
    setShowPositionModal(true);
  };

  const handleApplyClick = (e, position) => {
    e.stopPropagation();
    
    if (!user?.userd?.uid) {
      alert('Please log in to apply for positions');
      return;
    }

    if (!canApplyToPosition(position.id)) {
      const status = getApplicationStatus(position.id);
      alert(`You have already applied for this position. Current status: ${status}`);
      return;
    }

    setSelectedPosition(position);
    setShowApplyModal(true);
  };

  const handleCompanyClick = (e, employerId) => {
    e.stopPropagation();
    // Navigate to company profile
    navigate(`/companies/${employerId}`);
  };

  const submitApplication = async () => {
    if (!user?.userd?.uid || !selectedPosition) return;

    const validation = validateApplicationForm(applicationForm);
    if (!validation.isValid) {
      alert('Please complete all required fields with minimum character requirements:\n' + 
            Object.values(validation.errors).join('\n'));
      return;
    }

    setSubmitting(true);
    try {
      const applicationData = {
        studentId: user.userd.uid,
        studentInternID: user.profile?.internID,
        positionId: selectedPosition.id,
        studentName: user.userd.displayName || `${user.userd.firstName} ${user.userd.lastName}`,
        studentEmail: user.userd.email,
        positionTitle: selectedPosition.name,
        employerId: selectedPosition.employer_id,
        employerEmail: selectedPosition.contactEmail,
        status: 'pending',
        appliedAt: new Date().toISOString(),
        // Include all form fields with character counts for analytics
        coverLetter: applicationForm.coverLetter.trim(),
        whyInterested: applicationForm.whyInterested.trim(),
        relevantExperience: applicationForm.relevantExperience.trim(),
        availability: applicationForm.availability.trim(),
        additionalInfo: applicationForm.additionalInfo.trim(),
        // Add metadata for tracking
        applicationMetadata: {
          coverLetterLength: applicationForm.coverLetter.trim().length,
          whyInterestedLength: applicationForm.whyInterested.trim().length,
          relevantExperienceLength: applicationForm.relevantExperience.trim().length,
          availabilityLength: applicationForm.availability.trim().length,
          additionalInfoLength: applicationForm.additionalInfo.trim().length,
          submittedAt: new Date().toISOString(),
          userAgent: navigator.userAgent
        }
      };

      await addDoc(collection(db, 'applications'), applicationData);
      
      // Update local state
      setUserApplications(prev => [{ ...applicationData, id: Date.now().toString() }, ...prev]);
      
      // Close modal and reset form
      setShowApplyModal(false);
      setSelectedPosition(null);
      resetApplicationForm();
      
      alert('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetApplicationForm = () => {
    setApplicationForm({
      coverLetter: '',
      whyInterested: '',
      relevantExperience: '',
      availability: '',
      additionalInfo: ''
    });
  };

  // Handle modal close with confirmation if form has content
  const handleModalClose = () => {
    const hasContent = Object.values(applicationForm).some(value => value.trim().length > 0);
    
    if (hasContent) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setShowApplyModal(false);
        setSelectedPosition(null);
        resetApplicationForm();
      }
    } else {
      setShowApplyModal(false);
      setSelectedPosition(null);
      resetApplicationForm();
    }
  };

  const PositionCard = ({ position }) => {
    const applicationStatus = getApplicationStatus(position.id);
    const canApply = canApplyToPosition(position.id);
    const employer = employers[position.employer_id];

    const getStatusBadge = () => {
      if (!applicationStatus) return null;
      
      switch (applicationStatus) {
        case 'pending':
          return (
            <div className="flex items-center bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium mb-2">
              <FaClock className="mr-1" />
              Applied - Pending
            </div>
          );
        case 'accepted':
          return (
            <div className="flex items-center bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium mb-2">
              <FaCheck className="mr-1" />
              Accepted
            </div>
          );
        case 'rejected':
          return (
            <div className="flex items-center bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium mb-2">
              <FaTimes className="mr-1" />
              Rejected
            </div>
          );
        case 'interview':
          return (
            <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium mb-2">
              <FaCalendar className="mr-1" />
              Interview Scheduled
            </div>
          );
        case 'withdrawn':
          return (
            <div className="flex items-center bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium mb-2">
              <FaExclamationTriangle className="mr-1" />
              Withdrawn
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div 
        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-200 cursor-pointer transform hover:-translate-y-1"
        onClick={() => handlePositionClick(position)}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-800 mb-2 hover:text-orange-600 transition-colors">
              {position.name}
            </h3>
            <div className="flex items-center text-gray-600 mb-2">
              <FaBuilding className="mr-2" />
              <button
                onClick={(e) => handleCompanyClick(e, position.employer_id)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                {employer?.companyName || employer?.name || 'Company Name'}
              </button>
            </div>
          </div>
          <div className="text-right">
            {getStatusBadge()}
            {position.isPaid && (
              <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                Paid
              </div>
            )}
          </div>
        </div>

        <p className="text-gray-600 mb-4 line-clamp-3">{position.description}</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {position.requiredSkills?.slice(0, 3).map((skill, index) => (
            <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
              {skill}
            </span>
          ))}
          {position.requiredSkills?.length > 3 && (
            <span className="text-gray-500 text-xs">+{position.requiredSkills.length - 3} more</span>
          )}
        </div>

        <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
          <div className="flex items-center">
            <FaMapMarkerAlt className="mr-1" />
            <span>{position.location}</span>
          </div>
          <div className="flex items-center">
            <FaClock className="mr-1" />
            <span>{position.duration}</span>
          </div>
        </div>

        <div className="flex items-center space-x-4 text-xs text-orange-800">
          {/* Application Deadline */}
          {position.applicationDeadline && (
            <div className="flex items-center">
              <FaCalendar className="mr-1 h-3 w-3" />
              <span>Deadline: {new Date(position.applicationDeadline).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {position.type}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePositionClick(position);
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
            >
              <FaEye className="mr-1" />
              View
            </button>
            
            {canApply ? (
              <button
                onClick={(e) => handleApplyClick(e, position)}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Apply Now
              </button>
            ) : (
              <button
                disabled
                className="bg-gray-300 text-gray-500 px-4 py-2 rounded-md text-sm font-medium cursor-not-allowed"
              >
                {applicationStatus === 'pending' ? 'Applied' : 
                applicationStatus === 'accepted' ? 'Accepted' :
                applicationStatus === 'rejected' ? 'Rejected' :
                'Already Applied'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const FilterPanel = () => (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 transition-all duration-300 ${
      showFilters ? 'block' : 'hidden'
    } lg:block`}>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Advanced Filters</h3>
          <div className="flex space-x-2">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center"
              >
                <FaTimes className="mr-1" />
                Clear All
              </button>
            )}
            <button
              onClick={() => setShowFilters(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Types</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Remote">Remote</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
            <select
              value={filters.duration}
              onChange={(e) => handleFilterChange('duration', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Durations</option>
              <option value="1-3 months">1-3 months</option>
              <option value="3-6 months">3-6 months</option>
              <option value="6-12 months">6-12 months</option>
              <option value="12+ months">12+ months</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compensation</label>
            <select
              value={filters.isPaid}
              onChange={(e) => handleFilterChange('isPaid', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Positions</option>
              <option value="true">Paid Only</option>
              <option value="false">Unpaid Only</option>
            </select>
          </div>
        </div>

        {areas.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Fields of Interest</label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {areas.map((area, index) => (
                <button
                  key={`${area}-${index}`}
                  onClick={() => handleFieldToggle(area)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filters.fields.includes(area)
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Minimized search bar component
  const MinimizedSearchBar = () => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              placeholder="Quick search..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>
          {hasActiveFilters && (
            <div className="flex items-center text-xs text-gray-600">
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                {[filters.searchTerm, filters.location, filters.type, filters.duration, 
                  filters.isPaid && 'Paid', ...filters.fields].filter(Boolean).length} active
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => setSearchAreaMinimized(false)}
          className="ml-3 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          title="Expand search"
        >
          <FaExpand className="text-sm" />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Search/Filter Area */}
      <div className={`fixed top-16 left-0 right-0 z-40 bg-gray-50 border-b border-gray-200 transition-all duration-300 ${
        searchAreaCollapsed ? 'transform -translate-y-full' : 'transform translate-y-0'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Header - Always visible */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Explore Internships</h1>
              <p className="text-gray-600 text-sm mt-1">
                {filteredPositions.length} internship{filteredPositions.length !== 1 ? 's' : ''} available
              </p>
            </div>
            <div className="flex space-x-2">
              {!searchAreaMinimized && (
                <button
                  onClick={() => setSearchAreaMinimized(true)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md border border-gray-300"
                  title="Minimize search"
                >
                  <FaCompress />
                </button>
              )}
              <button
                onClick={() => setSearchAreaCollapsed(true)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md border border-gray-300"
                title="Hide search area"
              >
                <FaChevronUp />
              </button>
            </div>
          </div>

          {/* Search Content - Minimized or Full */}
          {searchAreaMinimized ? (
            <MinimizedSearchBar />
          ) : (
            <div className="space-y-4">
              {/* Search and Location bars */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    placeholder="Search positions, skills, or companies..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  />
                </div>

                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    placeholder="Enter location..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  />
                </div>
              </div>

              {/* Filter toggle and panel */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaFilter className="mr-2" />
                  Advanced Filters
                  {hasActiveFilters && (
                    <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                      {[filters.type, filters.duration, filters.isPaid && 'Paid', ...filters.fields]
                        .filter(Boolean).length}
                    </span>
                  )}
                  {showFilters ? <FaChevronUp className="ml-2" /> : <FaChevronDown className="ml-2" />}
                </button>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-orange-500 hover:text-orange-600 text-sm font-medium"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              {/* Filter Panel */}
              <FilterPanel />
            </div>
          )}
        </div>
      </div>

      {/* Show/Hide Search Area Button (when collapsed) */}
      {searchAreaCollapsed && (
        <button
          onClick={() => setSearchAreaCollapsed(false)}
          className="fixed top-20 right-4 z-40 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg transition-colors"
          title="Show search area"
        >
          <FaChevronDown />
        </button>
      )}

      {/* Main Content - with dynamic top padding */}
      <div className={`transition-all duration-300 ${
        searchAreaCollapsed 
          ? 'pt-16 pb-16' // Only header and footer spacing
          : searchAreaMinimized 
            ? 'pt-48 pb-16' // Header + minimized search + footer
            : showFilters 
              ? 'pt-80 pb-16' // Header + full search + filters + footer
              : 'pt-64 pb-16' // Header + full search + footer
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          {/* Results Grid */}
          {filteredPositions.length === 0 ? (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No positions found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters or search terms to find more opportunities.
                </p>
                <button
                  onClick={clearFilters}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md font-medium"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPositions.map((position) => (
                <PositionCard key={position.id} position={position} />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Position Detail Modal */}
      {showPositionModal && selectedPosition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedPosition.name}</h2>
                  <div className="flex items-center space-x-4">
                    <FaBuilding className="text-blue-600 mr-1" />
                        <div>
                          <button
                            onClick={(e) => handleCompanyClick(e, selectedPosition.employer_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {employers[selectedPosition.employer_id]?.companyName || 
                            employers[selectedPosition.employer_id]?.name || 'Company Name'}
                          </button>
                          {employers[selectedPosition.employer_id]?.industry && (
                            <p className="text-sm text-gray-600">
                              Industry: {employers[selectedPosition.employer_id].industry}
                            </p>
                          )}
                        </div>
                    {selectedPosition.isPaid && (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        <FaDollarSign className="inline mr-1" />
                        Paid Position
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowPositionModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Quick Details Grid */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Position Overview</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-medium">{selectedPosition.location || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaClock className="text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Type</p>
                        <p className="font-medium">{selectedPosition.type || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaCalendar className="text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="font-medium">{selectedPosition.duration || 'Not specified'}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaDollarSign className="text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-600">Compensation</p>
                        <p className="font-medium">{selectedPosition.isPaid ? 'Paid' : 'Unpaid'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Application Deadline */}
                  {selectedPosition.applicationDeadline && (
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center">
                        <FaExclamationTriangle className="text-orange-500 mr-2" />
                        <span className="text-orange-800 font-medium">
                          Application Deadline: {new Date(selectedPosition.applicationDeadline).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Position Description */}
                {selectedPosition.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">About This Position</h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {selectedPosition.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* Training & Learning Opportunities */}
                {selectedPosition.trainingToBeProvided && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Training & Learning Opportunities</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-900 leading-relaxed whitespace-pre-line">
                        {selectedPosition.trainingToBeProvided}
                      </p>
                    </div>
                  </div>
                )}

                {/* Requirements & Qualifications */}
                {selectedPosition.requirements && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements & Qualifications</h3>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-orange-900 leading-relaxed whitespace-pre-line">
                        {selectedPosition.requirements}
                      </p>
                    </div>
                  </div>
                )}

                {/* Skills & Areas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Required Skills */}
                  {selectedPosition.requiredSkills && selectedPosition.requiredSkills?.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Required Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedPosition.requiredSkills?.map((skill, index) => (
                          <span 
                            key={index} 
                            className="bg-blue-100 text-blue-800 px-3 py-2 rounded-full text-sm font-medium"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  {selectedPosition.benefits && selectedPosition.benefits.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Benefits</h4>
                      <p className="text-gray-700">{selectedPosition.benefits}</p>
                    </div>
                  )}
                </div>

                {/* Additional Information */}
                {/* Start Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedPosition.opensWhen && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Start Date</h4>
                      <p className="text-lg font-bold text-green-600">
                        {new Date(selectedPosition.opensWhen).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  )}

                  {/* Work Schedule */}
                  {selectedPosition.workSchedule && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Work Schedule</h4>
                      <p className="text-gray-700">{selectedPosition.workSchedule}</p>
                    </div>
                  )}
                </div>

                {/* Remote friendly */}
                <div className="text-center text-sm text-blue-500 border-t pt-4">
                  <p>{selectedPosition.remoteFriendly ? 'Remote friendly' : 'Not remote friendly'}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
                <button
                  onClick={() => setShowPositionModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Close
                </button>
                
                {canApplyToPosition(selectedPosition.id) ? (
                  <button
                    onClick={() => {
                      setShowPositionModal(false);
                      setShowApplyModal(true);
                    }}
                    className="px-8 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-medium flex items-center"
                  >
                    <FaCheck className="mr-2" />
                    Apply Now
                  </button>
                ) : (
                  <button
                    disabled
                    className="px-8 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed font-medium"
                  >
                    Already Applied
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {showApplyModal && selectedPosition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                Apply for {selectedPosition.name}
              </h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600"
                disabled={submitting}
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {/* REPLACE EXISTING FORM WITH ENHANCED VERSION */}
              <EnhancedApplicationForm
                applicationForm={applicationForm}
                setApplicationForm={setApplicationForm}
                onSubmit={submitApplication}
                submitting={submitting}
                selectedPosition={selectedPosition}
                onCancel={handleModalClose}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Explore;